import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import PanelTemplate from "../../templates/Panel";
import Box from "../../components/Box";
import FeedLogo from "./FeedLogo";
import CopyableUrl from "./CopyableUrl";
import { toast } from "react-toastify";
import api from "../../services/api";
import { getFeedChannel, buildFeedUrl } from "./feeds.config";
import { hasPlanFeature } from "../../services/auth";
import PlanFeatureUpgradeModal from "../../components/PlanFeatureUpgradeModal";
import "./styles.scss";

const formatDate = (value) =>
    value ? new Date(value).toLocaleString("pt-BR") : "—";

const FeedDetail = () => {
    const { slug } = useParams();
    const navigate = useNavigate();
    const channel = getFeedChannel(slug);

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [feed, setFeed] = useState(null);

    const [enabled, setEnabled] = useState(false);
    const [categoryMode, setCategoryMode] = useState("all");
    const [included, setIncluded] = useState([]);
    const [categories, setCategories] = useState([]);

    const [count, setCount] = useState(null);
    const [countLoading, setCountLoading] = useState(false);
    const [showUpgrade, setShowUpgrade] = useState(false);

    // Carrega config do feed + categorias pai.
    useEffect(() => {
        if (!channel) {
            setLoading(false);
            return;
        }

        Promise.all([api.get(`/catalog-feeds/${slug}`), api.get(`/category`)])
            .then(([feedRes, catRes]) => {
                const resource = feedRes.data.resource;
                setFeed(resource);
                setEnabled(resource.enabled);
                setCategoryMode(resource.category_mode);
                setIncluded(resource.category_ids || []);
                // Só listamos categorias pai (raiz); marcar um pai já inclui toda
                // a subárvore no backend, então não guardamos exclusões.
                setCategories(catRes.data.resources || []);
            })
            .finally(() => setLoading(false));
    }, [slug, channel]);

    // Contagem ao vivo de produtos elegíveis (debounced).
    useEffect(() => {
        if (loading || !channel) return;

        setCountLoading(true);
        const timer = setTimeout(() => {
            const params = new URLSearchParams();
            params.append("category_mode", categoryMode);
            if (categoryMode === "selected") {
                included.forEach((id) => params.append("category_ids[]", id));
            }

            api.get(`/catalog-feeds/product-count?${params.toString()}`)
                .then((res) => setCount(res.data.count))
                .finally(() => setCountLoading(false));
        }, 400);

        return () => clearTimeout(timer);
    }, [categoryMode, included, loading, channel, slug]);

    // Enquanto o feed está sendo (re)gerado na fila (last_status === 'generating'),
    // faz polling curto até o backend gravar 'ok'/'error' e então atualiza o Status
    // com os números reais. Cobre tanto o salvar quanto o agendamento de 3h.
    useEffect(() => {
        if (feed?.last_status !== "generating") return;

        let attempts = 0;
        const maxAttempts = 20; // ~60s a cada 3s

        const timer = setInterval(() => {
            attempts += 1;
            api.get(`/catalog-feeds/${slug}`)
                .then((res) => {
                    setFeed(res.data.resource);
                    if (res.data.resource.last_status !== "generating" || attempts >= maxAttempts) {
                        clearInterval(timer);
                    }
                })
                .catch(() => {
                    if (attempts >= maxAttempts) clearInterval(timer);
                });
        }, 3000);

        return () => clearInterval(timer);
    }, [feed?.last_status, slug]);

    const toggleEnabled = () => {
        const next = !enabled;

        // Ativar feed exige o recurso de plano 'catalog_feed'. Sem ele, mostra o
        // modal de upgrade em vez de chamar a API (o backend também bloqueia).
        if (next && !hasPlanFeature("catalog_feed")) {
            setShowUpgrade(true);
            return;
        }

        setEnabled(next);
        api.put(`/catalog-feeds/${slug}`, { enabled: next })
            .then((res) => {
                setFeed(res.data.resource);
                toast.success(next ? "Feed ativado" : "Feed desativado");
            })
            .catch(() => {
                setEnabled(!next);
                toast.error("Não foi possível alterar o feed");
            });
    };

    const saveCategories = () => {
        setSaving(true);
        api.put(`/catalog-feeds/${slug}`, {
            category_mode: categoryMode,
            category_ids: categoryMode === "selected" ? included : [],
            category_excludes: [],
        })
            .then((res) => {
                setFeed(res.data.resource);
                toast.success("Alterações salvas com sucesso");
            })
            .catch(() => toast.error("Não foi possível salvar as alterações"))
            .finally(() => setSaving(false));
    };

    if (!channel) {
        return (
            <PanelTemplate id="feed-detail">
                <div className="feed-page">
                    <button className="feed-back" onClick={() => navigate("/integration/list?tab=feeds")}>
                        ← Voltar para feeds
                    </button>
                    <Box title="Feed não encontrado">
                        <p>O feed solicitado não existe.</p>
                    </Box>
                </div>
            </PanelTemplate>
        );
    }

    const url = feed?.feed_url || buildFeedUrl(channel.slug);

    // O backend exige category_ids quando o modo é "selected"; bloqueamos o
    // salvar (em vez de deixar cair num 412 genérico) enquanto nada estiver
    // incluído.
    const noSelection = categoryMode === "selected" && included.length === 0;

    return (
        <PanelTemplate id="feed-detail">
            <div className="feed-page feed-detail">
                <button className="feed-back" onClick={() => navigate("/integration/list?tab=feeds")}>
                    ← Voltar para feeds
                </button>

                <div className="feed-detail__header">
                    <FeedLogo slug={channel.slug} color={channel.color} size={64} />
                    <div className="feed-detail__title">
                        <span className="feed-card__tag">{channel.category}</span>
                        <h1>{channel.name}</h1>
                        <p>{channel.description}</p>
                    </div>
                    <label className="feed-switch feed-switch--lg" title={enabled ? "Desativar feed" : "Ativar feed"}>
                        <input type="checkbox" checked={enabled} onChange={toggleEnabled} disabled={loading} />
                        <span className="feed-switch__track" />
                    </label>
                </div>

                <div className="feed-detail__grid">
                    <div className="feed-detail__main">
                        <Box title="URL do feed">
                            <p className="feed-detail__hint">
                                Esta é a URL pública que o {channel.name} consulta. Formato:{" "}
                                <strong>{channel.format}</strong>.
                            </p>
                            <CopyableUrl url={url} />
                        </Box>

                        <Box title="Categorias no feed">
                            <div className="feed-radio-group">
                                <label className="feed-radio">
                                    <input
                                        type="radio"
                                        name="category_mode"
                                        checked={categoryMode === "all"}
                                        onChange={() => setCategoryMode("all")}
                                    />
                                    <span>
                                        <strong>Todas as categorias</strong>
                                        Envia todo o catálogo ativo e com estoque.
                                    </span>
                                </label>
                                <label className="feed-radio">
                                    <input
                                        type="radio"
                                        name="category_mode"
                                        checked={categoryMode === "selected"}
                                        onChange={() => setCategoryMode("selected")}
                                    />
                                    <span>
                                        <strong>Categorias específicas</strong>
                                        Marque as categorias que deseja enviar. Cada categoria
                                        inclui automaticamente todas as suas subcategorias.
                                    </span>
                                </label>
                            </div>

                            {categoryMode === "selected" && (
                                <div className="feed-categories">
                                    {categories.map((cat) => {
                                        const checked = included.includes(cat.id);
                                        return (
                                            <label key={cat.id} className="feed-cat-item">
                                                <input
                                                    type="checkbox"
                                                    checked={checked}
                                                    onChange={() =>
                                                        setIncluded((prev) =>
                                                            checked
                                                                ? prev.filter((id) => id !== cat.id)
                                                                : [...prev, cat.id]
                                                        )
                                                    }
                                                />
                                                <span>{cat.name}</span>
                                            </label>
                                        );
                                    })}
                                </div>
                            )}

                            <div className="feed-count">
                                {countLoading ? (
                                    "Calculando produtos…"
                                ) : (
                                    <>
                                        <b>{count ?? 0}</b> produto(s) serão enviados neste feed.
                                    </>
                                )}
                            </div>

                            {noSelection && (
                                <p className="feed-save-hint">
                                    Selecione ao menos uma categoria para salvar.
                                </p>
                            )}

                            <button
                                className="feed-save"
                                onClick={saveCategories}
                                disabled={saving || loading || noSelection}
                            >
                                {saving ? "Salvando…" : "Salvar alterações"}
                            </button>
                        </Box>

                        <Box title="Como conectar">
                            {channel.instructions?.length > 0 && (
                                <ol className="feed-steps">
                                    {channel.instructions.map((step, i) => (
                                        <li key={i}>{step}</li>
                                    ))}
                                </ol>
                            )}
                            {!channel.instructions?.length && channel.docsUrl && (
                                <p className="feed-docs-intro">
                                    Para saber como configurar, acesse a documentação oficial:
                                </p>
                            )}
                            {channel.docsUrl && (
                                <a
                                    className="feed-docs-link"
                                    href={channel.docsUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    Ver documentação oficial →
                                </a>
                            )}
                        </Box>
                    </div>

                    <div className="feed-detail__side">
                        <Box title="Status">
                            {feed?.last_status === "generating" && (
                                <div className="feed-status-banner feed-status-banner--generating">
                                    <span className="feed-status-spinner" />
                                    Gerando feed… os números abaixo são da última geração e
                                    serão atualizados em instantes.
                                </div>
                            )}
                            {feed?.last_status === "error" && (
                                <div className="feed-status-banner feed-status-banner--error">
                                    Falha na última geração do feed. Tente salvar novamente.
                                </div>
                            )}
                            <ul className="feed-meta">
                                <li>
                                    <span>Situação</span>
                                    <b className={enabled ? "on" : "off"}>{enabled ? "Ativo" : "Inativo"}</b>
                                </li>
                                <li>
                                    <span>Última atualização</span>
                                    <b>{formatDate(feed?.last_generated_at)}</b>
                                </li>
                                <li>
                                    <span>Produtos no feed</span>
                                    <b>{feed?.item_count ?? "—"}</b>
                                </li>
                            </ul>
                        </Box>
                    </div>
                </div>
            </div>

            <PlanFeatureUpgradeModal
                feature="catalog_feed"
                show={showUpgrade}
                close={() => setShowUpgrade(false)}
            />
        </PanelTemplate>
    );
};

export default FeedDetail;
